import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Divider,
  CircularProgress,
  Alert,
} from "@mui/material";
import type { ProductVariant } from "../types";
import { capFirst } from "../utils/groupVariants";
import AddProductsFromExcelSheet from "../components/addProductsFromExcelSheet";
import UploadProductImage from "../components/uploadProductImage";

const adminApiUrl = process.env.REACT_APP_API_URL;

const AdminPage = () => {
  const [products, setProducts] = useState<ProductVariant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await fetch(`${adminApiUrl}/api/v1/products`);
        if (!res.ok) throw new Error("Failed to fetch products");
        setProducts(await res.json());
      } catch (err) {
        setError(err instanceof Error ? err.message : "Unknown error");
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  return (
    <Box sx={{ maxWidth: "64rem", mx: "auto", px: 4, py: 8 }}>
      <Typography
        variant="h5"
        sx={{
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.1em",
          mb: 6,
        }}
      >
        Admin Dashboard
      </Typography>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 3,
          mb: 8,
        }}
      >
        {[
          { label: "Total Variants", value: products.length },
          {
            label: "With Image",
            value: products.filter((p) => !!p.image).length,
          },
          {
            label: "Unique Products",
            value: new Set(products.map((p) => p.name)).size,
          },
        ].map(({ label, value }) => (
          <Paper
            key={label}
            elevation={0}
            sx={{ p: 3, border: "1px solid #e5e5e5", borderRadius: 0 }}
          >
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
              {loading ? "—" : value}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: "#737373",
                textTransform: "uppercase",
                letterSpacing: "0.1em",
              }}
            >
              {label}
            </Typography>
          </Paper>
        ))}
      </Box>

      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.1em",
          mb: 3,
        }}
      >
        Products
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 0 }}>
          {error}
        </Alert>
      )}

      <Divider sx={{ my: 6 }} />
      <AddProductsFromExcelSheet />
      <Divider sx={{ my: 6 }} />
      <UploadProductImage />
      <Divider sx={{ my: 6 }} />

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress sx={{ color: "black" }} />
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
          {products.length === 0 ? (
            <Typography
              variant="body2"
              sx={{ color: "#737373", py: 4, textAlign: "center" }}
            >
              No products found. Add products via the API.
            </Typography>
          ) : (
            products.map((variant, i) => (
              <Box
                key={variant.id}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr auto auto",
                  alignItems: "center",
                  gap: 3,
                  py: 2,
                  borderBottom: "1px solid #e5e5e5",
                  borderTop: i === 0 ? "1px solid #e5e5e5" : "none",
                }}
              >
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {capFirst(variant.name)}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#737373" }}>
                    {variant.size}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: "#111" }}>
                  {variant.salePrice != null ? `$${variant.salePrice}` : "—"}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: variant.image ? "#16a34a" : "#a3a3a3" }}
                >
                  {variant.image ? "Has image" : "No image"}
                </Typography>
              </Box>
            ))
          )}
        </Box>
      )}
    </Box>
  );
};

export default AdminPage;
